import React, { useEffect, useRef, useState } from 'react';
import './TalkingHeadAvatar.scss';
import { useLiveAPIContext } from '../../contexts/LiveAPIContext';

interface TalkingHeadAvatarProps {
    avatarUrl?: string; // e.g. "/avatars/julia.glb"
}

export const TalkingHeadAvatar: React.FC<TalkingHeadAvatarProps> = ({
    avatarUrl = "/avatars/julia.glb"
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const headRef = useRef<any>(null);
    const headAudioRef = useRef<any>(null);
    // audioStreamer might be null initially
    const { audioStreamer } = useLiveAPIContext();
    const [isLoading, setIsLoading] = useState(true);
    const [loadingStatus, setLoadingStatus] = useState("Initializing...");

    // Ref to track if we have already initialized to prevent double init
    const initializedRef = useRef(false);

    useEffect(() => {
        // Wait for container and audioStreamer
        const container = containerRef.current;
        if (!container || !audioStreamer || initializedRef.current) return;

        const init = async () => {
            initializedRef.current = true;
            try {
                setLoadingStatus("Loading modules...");

                // Dynamically import modules to avoid Webpack bundling issues
                // @ts-ignore
                const { TalkingHead } = await import(/* webpackIgnore: true */ "talkinghead");
                // @ts-ignore
                const { HeadAudio } = await import(/* webpackIgnore: true */ "headaudio");

                setLoadingStatus("Starting 3D Engine...");

                // 1. Initialize TalkingHead
                const head = new TalkingHead(container, {
                    ttsEndpoint: "N/A",
                    lipsyncModules: [], // We use HeadAudio for lipsync
                    cameraView: "upper",
                    cameraDistance: 0.3,
                    cameraX: -0.08,
                    cameraY: 0.3,
                    cameraRotateEnable: false,
                    lightAmbientIntensity: 0.8,
                    lightDirectIntensity: 0.5,
                    avatarIdleEyeContact: 1,
                    avatarSpeakingEyeContact: 1,
                    avatarIdleHeadMove: 0,
                    avatarSpeakingHeadMove: 0,
                    avatarMood: "neutral",
                    avatarIgnoreCamera: true
                });

                headRef.current = head;

                head.start();

                const updateInfo = (node: string, percent: any) => {
                    if (percent && percent.lengthComputable !== undefined) {
                        const val = Math.round(percent.loaded / percent.total * 100);
                        setLoadingStatus(`Loading Avatar: ${val}%`);
                    } else if (typeof percent === 'string') {
                        setLoadingStatus(`Loading: ${percent}`);
                    }
                };

                setLoadingStatus("Loading Avatar Model...");
                await head.showAvatar({
                    url: avatarUrl,
                    body: "F",
                    avatarMood: "neutral",
                    lipsyncLang: 'en' // Important for viseme mapping
                }, (ev: any) => updateInfo("avatar", ev));

                // Set static mood after avatar is loaded
                // Character breathes and moves body slightly, but head and eyes stay focused on user
                (head as any).animMoods['still'] = {
                    baseline: {
                        eyesLookDown: 0,
                        eyesRotateX: 0, // Force eyes to look straight
                        eyesRotateY: 0  // Force eyes to look straight
                    },
                    speech: { deltaRate: 0, deltaPitch: 0, deltaVolume: 0 },
                    anims: [
                        { name: 'pose', vs: { pose: ['straight'] } },
                        {
                            name: 'eyes',
                            dt: [5000],
                            vs: {
                                eyesRotateY: [0],
                                eyesRotateX: [0],
                                eyeContact: [1],
                                headMove: [0]
                            }
                        },
                        {
                            name: 'head',
                            dt: [[2000, 5000]],
                            vs: {
                                headRotateX: [0], headRotateY: [0], headRotateZ: [0],
                                bodyRotateX: [[-0.02, 0.04]], bodyRotateY: [[-0.05, 0.05]], bodyRotateZ: [[-0.02, 0.02]]
                            }
                        },
                        { name: 'breathing', delay: 1500, dt: [1200, 500, 1000], vs: { chestInhale: [0.4, 0.4, 0] } },
                        { name: 'blink', delay: [1000, 8000], dt: [50, [100, 300], 100], vs: { eyeBlinkLeft: [1, 1, 0], eyeBlinkRight: [1, 1, 0] } }
                    ]
                };
                head.setMood('still');

                // 2. Initialize HeadAudio (with graceful fallback)
                try {
                    setLoadingStatus("Initializing Audio Engine...");
                    const audioCtx = audioStreamer.context;

                    // Mobile safety: ensure AudioContext exists and is running
                    if (audioCtx && audioCtx.audioWorklet) {
                        if (audioCtx.state === 'suspended') {
                            await audioCtx.resume();
                        }

                        // Add worklet
                        try {
                            await audioCtx.audioWorklet.addModule("/headaudio/headworklet.min.mjs");
                        } catch (e: any) {
                            // It might be already added if component re-mounted
                            console.warn("Worklet addModule error (might be already added):", e);
                        }

                        const headaudio = new HeadAudio(audioCtx);
                        headAudioRef.current = headaudio;

                        // Load model
                        await headaudio.loadModel("/headaudio/model-en-mixed.bin");

                        // Connect AudioStreamer output to HeadAudio
                        // AudioStreamer.gainNode is the node that plays audio. 
                        // We connect it to HeadAudio to analyze what's being played.
                        audioStreamer.gainNode.connect(headaudio);

                        // 3. Link them together
                        headaudio.onvalue = (key: string, value: number) => {
                            if (head && head.mtAvatar && head.mtAvatar[key]) {
                                head.mtAvatar[key].newvalue = value;
                                head.mtAvatar[key].needsUpdate = true;
                            }
                        };

                        // Hook update loop
                        // TalkingHead's update loop calls this function
                        head.opt.update = (dt: number) => {
                            headaudio.update(dt);
                        };

                        console.log("TalkingHeadAvatar audio engine initialized.");
                    } else {
                        console.warn("AudioContext not ready/supported, skipping lipsync.");
                    }
                } catch (audioErr) {
                    console.error("Audio engine init failed (continuing without lipsync):", audioErr);
                }

                console.log("TalkingHeadAvatar fully initialized.");
                setLoadingStatus(""); // Clear loading status
                setIsLoading(false);

            } catch (err: any) {
                console.error("Failed to init TalkingHeadAvatar:", err);
                setLoadingStatus("Error: " + err.message);
                initializedRef.current = false; // Allow retry?
            }
        };

        init();

        return () => {
            // Cleanup
            if (headRef.current) {
                headRef.current.stop();
            }
            if (headAudioRef.current) {
                try {
                    headAudioRef.current.disconnect();
                    if (audioStreamer && audioStreamer.gainNode) {
                        audioStreamer.gainNode.disconnect(headAudioRef.current);
                    }
                } catch (e) { /* ignore */ }
            }
            initializedRef.current = false;
        };
    }, [avatarUrl, audioStreamer]);

    return (
        <div
            className="talking-head-container"
            style={{
                backgroundImage: "url('/background.png')",
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat'
            }}
        >
            <div id="talking-head-avatar" ref={containerRef} style={{ width: '100%', height: '100%' }} />
            {isLoading && (
                <div className="loading-overlay">
                    <span>{loadingStatus}</span>
                </div>
            )}
        </div>
    );
};
